import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, getFirestore, runTransaction } from 'firebase/firestore';
import { deleteObject, getStorage, ref } from 'firebase/storage';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { auth } from '../firebase';
import { isActiveLabelingWork } from './labelStatus';
import {
  TANNER_GENDERS,
  TANNER_STAGES,
  applyMetadataPatch,
  canDeleteUploadedRecord,
  emptyStimulusMetadata,
  stimuliBackendBaseUrl,
  summarizeBatch,
  uploadStimulus,
  validateStimulusMetadata,
} from './stimulusUploads';

const STEPS = ['Select images', 'Describe images', 'Upload', 'Done'];

let nextItemId = 1;

const SETTLED_STATUSES = ['success', 'error'];

// Slack for client/backend clock drift in the uploaded_at ownership check.
const OWNERSHIP_CLOCK_SKEW_MS = 5 * 60 * 1000;

// Deletes images/{hash} atomically, and only while this admin still owns the
// record. The backend rewrites uploaded_by and uploaded_at on EVERY upload,
// including merge re-uploads, so a record that another user (or a later
// session) has re-uploaded since this batch created it fails the check and
// survives. `notBefore` pins ownership to this batch: an uploaded_at older
// than the batch's first upload means some earlier write owns the doc.
// Known limitation: the same admin uploading the same image from a second
// tab rewrites uploaded_by with the SAME uid and a fresh uploaded_at, which
// is indistinguishable from this batch's own re-upload — deleting here can
// still orphan the other tab's reference. Only the backend could close that
// gap (per-request lease tokens).
async function deleteStimulusDocIfOwned(hash, uid, notBefore) {
  const db = getFirestore();
  const docRef = doc(db, 'images', hash);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(docRef);
    if (!snapshot.exists()) {
      return 'deleted';
    }
    const data = snapshot.data() || {};
    if (!uid || data.uploaded_by !== uid) {
      return 'not-owned';
    }
    const uploadedAt = typeof data.uploaded_at?.toDate === 'function'
      ? data.uploaded_at.toDate()
      : null;
    if (notBefore && uploadedAt && uploadedAt.getTime() < notBefore.getTime()) {
      return 'not-owned';
    }
    transaction.delete(docRef);
    return 'deleted';
  });
}

export default function AddStimuliWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [batchMeta, setBatchMeta] = useState(emptyStimulusMetadata());
  const [labelStatus, setLabelStatus] = useState({});
  const [labelStatusLoading, setLabelStatusLoading] = useState(false);
  const [cleanupWarning, setCleanupWarning] = useState('');

  // Hashes whose images/{hash} doc THIS batch created, established the first
  // time any item's upload response reports created:true and never revoked by
  // later created:false responses or twin removals — so removing the last
  // item of a hash can still clean up, whichever twin uploaded first.
  const ownedHashesRef = useRef(new Set());

  // Captured (minus clock slack) just before the batch's first upload; used
  // to pin the uploaded_at ownership check to this batch.
  const batchStartedAtRef = useRef(null);

  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Revoke object URLs when the wizard unmounts.
  useEffect(() => () => {
    itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
  }, []);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    if (incoming.length === 0) {
      return;
    }
    setItems((prev) => {
      const seen = new Set(prev.map((item) => `${item.file.name}|${item.file.size}|${item.file.lastModified}`));
      const added = incoming
        .filter((file) => !seen.has(`${file.name}|${file.size}|${file.lastModified}`))
        .map((file) => ({
          id: nextItemId++,
          file,
          previewUrl: URL.createObjectURL(file),
          metadata: emptyStimulusMetadata(),
          status: 'pending',
          progress: 0,
          error: null,
          hash: null,
          blobOrphaned: false,
        }));
      return [...prev, ...added];
    });
  }, []);

  // Removing an item that has uploaded must not leave a backend stimulus the
  // wizard forgets about: a record this batch created (per the batch-level
  // ownership set) is deleted after a confirm — atomically, and only while
  // this admin still owns it server-side; a record that already existed or is
  // shared with another batch item stays, and the user is told which. The
  // backend has no delete endpoint, but the SPA already writes these
  // Firestore docs directly (labels in ViewStimulusDetail), so the wizard
  // cleans up records it created the same way — doc first (it drives the
  // queue and list), then the blob.
  const removeItem = useCallback(async (id) => {
    const item = itemsRef.current.find((candidate) => candidate.id === id);
    if (!item) {
      return;
    }

    if (item.blobOrphaned && item.hash) {
      // The doc is already gone; only the storage blob is left to retry.
      if (!window.confirm('The stimulus record was already deleted, but its image file is still in storage. Try deleting the image file again?')) {
        return;
      }
      try {
        setCleanupWarning('');
        await deleteObject(ref(getStorage(), `vasi/stimuli/${item.hash}.jpg`));
      } catch (error) {
        if (error?.code !== 'storage/object-not-found') {
          console.error('Blob cleanup retry failed:', error);
          setCleanupWarning(`Still could not remove the image file for ${item.hash.slice(0, 12)}… (${error.message || 'unknown error'}). Remove the file again to retry, or clean up vasi/stimuli/${item.hash}.jpg manually.`);
          return;
        }
      }
    } else if (item.hash) {
      const deletable = canDeleteUploadedRecord(item, itemsRef.current, ownedHashesRef.current);
      const sharedWithOther = itemsRef.current.some(
        (other) => other.id !== id && other.hash === item.hash,
      );
      const message = deletable
        ? 'This file was uploaded. Removing it will DELETE the uploaded stimulus (image and metadata) from the system. Continue?'
        : sharedWithOther
          ? 'Another file in this batch produced the same stimulus, so the stimulus stays in the system. Remove this file from the batch?'
          : 'This file matched a stimulus that already existed in the system (or the backend did not confirm creating it), so the stimulus stays. Remove the file from this batch?';
      if (!window.confirm(message)) {
        return;
      }
      if (deletable) {
        const shortHash = `${item.hash.slice(0, 12)}…`;
        let outcome;
        try {
          setCleanupWarning('');
          outcome = await deleteStimulusDocIfOwned(
            item.hash,
            auth.currentUser?.uid,
            batchStartedAtRef.current,
          );
        } catch (error) {
          console.error('Failed to delete uploaded stimulus:', error);
          setCleanupWarning(`Could not delete uploaded stimulus ${shortHash} (${error.message || 'unknown error'}). The file was kept in the batch — try removing it again, or clean the stimulus up manually.`);
          return;
        }
        ownedHashesRef.current.delete(item.hash);
        if (outcome === 'not-owned') {
          // Another user or session re-uploaded this stimulus since the batch
          // created it — the record is theirs now and stays in the system.
          setCleanupWarning(`Stimulus ${shortHash} has been re-uploaded by someone else since this batch created it, so it stays in the system.`);
        } else {
          try {
            await deleteObject(ref(getStorage(), `vasi/stimuli/${item.hash}.jpg`));
          } catch (error) {
            if (error?.code !== 'storage/object-not-found') {
              // The doc is gone (the stimulus no longer exists for the app)
              // but the blob is orphaned. Keep the item — no longer marked
              // uploaded — so removing it again retries just the blob delete.
              console.error('Deleted stimulus doc but failed to delete its blob:', error);
              updateItem(id, {
                status: 'error',
                progress: 0,
                blobOrphaned: true,
                error: 'Stimulus record deleted, but its image file could not be removed from storage. Remove again to retry.',
              });
              setCleanupWarning(`Deleted stimulus record ${shortHash}, but its image file could not be removed from storage (${error.message || 'unknown error'}). Remove the file again to retry, or clean up vasi/stimuli/${item.hash}.jpg manually.`);
              return;
            }
          }
        }
      }
    }

    setItems((prev) => {
      const removed = prev.find((candidate) => candidate.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return prev.filter((candidate) => candidate.id !== id);
    });
  }, [updateItem]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragActive(false);
    addFiles(event.dataTransfer.files);
  }, [addFiles]);

  const updateMetadata = useCallback((id, patch) => {
    setItems((prev) => prev.map((item) => (
      item.id === id ? applyMetadataPatch(item, patch) : item
    )));
  }, []);

  const applyBatchMetadata = useCallback(() => {
    setItems((prev) => prev.map((item) => applyMetadataPatch(item, batchMeta)));
  }, [batchMeta]);

  const validationById = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(item.id, validateStimulusMetadata(item.metadata)));
    return map;
  }, [items]);

  const allMetadataValid = items.length > 0
    && items.every((item) => (validationById.get(item.id) || []).length === 0);

  // A re-upload can land on a DIFFERENT hash than the item's earlier upload
  // (the file's bytes changed on disk, or the source file was swapped). The
  // old record this batch created would be silently orphaned — clean it up,
  // with the same ownership transaction guarding against concurrent claims.
  const cleanupSupersededRecord = useCallback(async (hash, excludeItemId) => {
    if (!ownedHashesRef.current.has(hash)) {
      return;
    }
    if (itemsRef.current.some((other) => other.id !== excludeItemId && other.hash === hash)) {
      return;
    }
    const shortHash = `${hash.slice(0, 12)}…`;
    try {
      const outcome = await deleteStimulusDocIfOwned(
        hash,
        auth.currentUser?.uid,
        batchStartedAtRef.current,
      );
      ownedHashesRef.current.delete(hash);
      if (outcome !== 'deleted') {
        return;
      }
      try {
        await deleteObject(ref(getStorage(), `vasi/stimuli/${hash}.jpg`));
      } catch (error) {
        if (error?.code !== 'storage/object-not-found') {
          console.error('Failed to delete superseded stimulus blob:', error);
          setCleanupWarning(`Removed superseded stimulus record ${shortHash}, but its image file could not be removed from storage (${error.message || 'unknown error'}). Clean up vasi/stimuli/${hash}.jpg manually.`);
        }
      }
    } catch (error) {
      console.error('Failed to clean up superseded stimulus:', error);
      setCleanupWarning(`A re-upload replaced stimulus ${shortHash}, but the old record could not be cleaned up (${error.message || 'unknown error'}). Clean it up manually.`);
    }
  }, []);

  const runUploads = useCallback(async (targetIds) => {
    setUploading(true);
    const baseUrl = stimuliBackendBaseUrl();

    if (!batchStartedAtRef.current) {
      batchStartedAtRef.current = new Date(Date.now() - OWNERSHIP_CLOCK_SKEW_MS);
    }

    // Always POST: the backend keys images/{sha256} by the bytes of the FINAL
    // transformed JPEG (not the raw upload), so the client cannot detect a
    // re-upload up front. Re-POSTing is safe — the backend is idempotent and
    // writes with merge=True, so existing labels survive.
    for (const id of targetIds) {
      const item = itemsRef.current.find((candidate) => candidate.id === id);
      if (!item) {
        continue;
      }
      try {
        updateItem(id, { status: 'uploading', progress: 0, error: null });
        const token = await auth.currentUser.getIdToken();
        const response = await uploadStimulus({
          baseUrl,
          token,
          file: item.file,
          metadata: item.metadata,
          onProgress: (progress) => updateItem(id, { progress }),
        });
        const uploadedHash = response.hash || null;
        // `created` is the backend's authoritative provenance: true iff THIS
        // request created the images/{hash} doc (atomic via a Firestore
        // create-precondition). A missing flag (older backend) counts as not
        // created — fail closed. Ownership is recorded per hash for the whole
        // batch, so a twin's or re-upload's created:false never revokes it.
        if (response.created === true && uploadedHash) {
          ownedHashesRef.current.add(uploadedHash);
        }
        const previousHash = item.hash;
        updateItem(id, {
          status: 'success',
          progress: 100,
          hash: uploadedHash,
          blobOrphaned: false,
        });
        if (previousHash && uploadedHash && previousHash !== uploadedHash) {
          await cleanupSupersededRecord(previousHash, id);
        }
      } catch (error) {
        console.error('Stimulus upload failed:', error);
        updateItem(id, { status: 'error', error: error.message || 'Upload failed.' });
      }
    }
    setUploading(false);
  }, [updateItem, cleanupSupersededRecord]);

  // Entering the upload step (or returning to it with newly added files)
  // uploads everything still pending; failed items re-run via the retry
  // buttons only.
  useEffect(() => {
    if (step !== 2 || uploading) {
      return;
    }
    const pendingIds = itemsRef.current
      .filter((item) => item.status === 'pending')
      .map((item) => item.id);
    if (pendingIds.length > 0) {
      runUploads(pendingIds);
    }
  }, [step, uploading, runUploads]);

  // Items eligible for an upload retry. A doc-deleted-but-blob-orphaned item
  // must NOT be re-POSTed (that would recreate the deleted record) — its only
  // affordance is Remove, which retries the blob cleanup.
  const failedItems = items.filter((item) => item.status === 'error' && !item.blobOrphaned);
  const allSettled = items.length > 0
    && !uploading
    && items.every((item) => SETTLED_STATUSES.includes(item.status));

  const batchTannerHashes = useMemo(() => Array.from(new Set(
    items
      .filter((item) => item.status === 'success'
        && item.metadata.type === 'tanner'
        && item.hash)
      .map((item) => item.hash),
  )), [items]);

  const refreshLabelStatus = useCallback(async () => {
    if (batchTannerHashes.length === 0) {
      setLabelStatus({});
      return;
    }
    try {
      setLabelStatusLoading(true);
      const db = getFirestore();
      const snapshots = await Promise.all(
        batchTannerHashes.map((hash) => getDoc(doc(db, 'images', hash))),
      );
      const status = {};
      snapshots.forEach((snapshot, index) => {
        status[batchTannerHashes[index]] = snapshot.exists()
          ? isActiveLabelingWork(snapshot.data())
          : true;
      });
      setLabelStatus(status);
    } catch (error) {
      console.error('Failed to load label status for batch:', error);
    } finally {
      setLabelStatusLoading(false);
    }
  }, [batchTannerHashes]);

  useEffect(() => {
    if (step === 3) {
      refreshLabelStatus();
    }
  }, [step, refreshLabelStatus]);

  const unlabeledBatchHashes = batchTannerHashes.filter((hash) => labelStatus[hash] !== false);
  const summary = summarizeBatch(items);

  const startLabeling = () => {
    navigate('/stimuli/queue', { state: { batchHashes: unlabeledBatchHashes } });
  };

  const handleContinueFromMetadata = () => {
    if (!allMetadataValid) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    setStep(2);
  };

  const statusBadge = (item) => {
    switch (item.status) {
      case 'success':
        return (
          <span className="inline-flex items-center gap-x-1 rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
            <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
            Uploaded
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-x-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
            Failed
          </span>
        );
      case 'uploading':
        return (
          <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
            Uploading {item.progress}%
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
            Waiting
          </span>
        );
    }
  };

  const metadataFields = (metadata, onChange, idPrefix, { lockType = false } = {}) => (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor={`${idPrefix}-type`} className="block text-xs font-medium text-gray-700">Type</label>
        <select
          id={`${idPrefix}-type`}
          value={metadata.type}
          disabled={lockType}
          title={lockType ? 'Type is locked after upload — remove this file and re-add it to change the type.' : undefined}
          onChange={(event) => onChange({ type: event.target.value })}
          className="mt-1 block w-32 rounded-md border-gray-300 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
        >
          <option value="tanner">Tanner</option>
          <option value="control">Control</option>
        </select>
      </div>
      {metadata.type === 'tanner' ? (
        <>
          <div>
            <label htmlFor={`${idPrefix}-gender`} className="block text-xs font-medium text-gray-700">Gender</label>
            <select
              id={`${idPrefix}-gender`}
              value={metadata.gender}
              onChange={(event) => onChange({ gender: event.target.value })}
              className="mt-1 block w-24 rounded-md border-gray-300 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
            >
              <option value="">Select…</option>
              {TANNER_GENDERS.map((gender) => (
                <option key={gender} value={gender}>{gender}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${idPrefix}-stage`} className="block text-xs font-medium text-gray-700">Tanner stage</label>
            <select
              id={`${idPrefix}-stage`}
              value={metadata.tanner}
              onChange={(event) => onChange({ tanner: event.target.value })}
              className="mt-1 block w-28 rounded-md border-gray-300 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
            >
              <option value="">Select…</option>
              {TANNER_STAGES.map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <>
          <div>
            <label htmlFor={`${idPrefix}-control`} className="block text-xs font-medium text-gray-700">Control #</label>
            <input
              id={`${idPrefix}-control`}
              type="number"
              min="0"
              value={metadata.control}
              onChange={(event) => onChange({ control: event.target.value })}
              className="mt-1 block w-24 rounded-md border-gray-300 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-control-group`} className="block text-xs font-medium text-gray-700">Control group</label>
            <input
              id={`${idPrefix}-control-group`}
              type="number"
              min="0"
              value={metadata.controlGroup}
              onChange={(event) => onChange({ controlGroup: event.target.value })}
              className="mt-1 block w-28 rounded-md border-gray-300 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 disabled:bg-gray-100"
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/stimuli')}
          className="inline-flex items-center gap-x-1.5 text-sm font-semibold text-gray-900"
        >
          <ArrowLeftIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Back to Stimuli
        </button>
      </div>

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Add Stimuli</h1>
          <p className="mt-2 text-sm text-gray-700">
            Upload a batch of stimulus images, describe each one, then label the Tanner images.
            New Tanner stimuli are enabled automatically; labeling remains a separate workflow.
          </p>
        </div>
      </div>

      {cleanupWarning && (
        <div role="alert" className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {cleanupWarning}
        </div>
      )}

      <nav aria-label="Progress" className="mt-6">
        <ol className="flex items-center gap-4">
          {STEPS.map((name, index) => (
            <li key={name} className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                  index < step
                    ? 'bg-indigo-600 text-white'
                    : index === step
                      ? 'border-2 border-indigo-600 text-indigo-600'
                      : 'border-2 border-gray-300 text-gray-500'
                }`}
              >
                {index + 1}
              </span>
              <span className={`text-sm font-medium ${index === step ? 'text-indigo-600' : 'text-gray-500'}`}>
                {name}
              </span>
            </li>
          ))}
        </ol>
      </nav>

      {step === 0 && (
        <div className="mt-8">
          <div
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 ${
              dragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300 bg-white'
            }`}
          >
            <PhotoIcon className="h-12 w-12 text-gray-400" aria-hidden="true" />
            <p className="mt-3 text-sm text-gray-600">
              Drag and drop stimulus images here, or
            </p>
            <label className="mt-2 cursor-pointer rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
              Browse files
              <input
                type="file"
                multiple
                accept="image/*"
                className="sr-only"
                onChange={(event) => {
                  addFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </label>
            <p className="mt-2 text-xs text-gray-500">JPEG or PNG. Images are resized to 240x360 by the backend.</p>
          </div>

          {items.length > 0 && (
            <>
              <p className="mt-6 text-sm font-medium text-gray-900">{items.length} file{items.length === 1 ? '' : 's'} selected</p>
              <ul className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
                {items.map((item) => (
                  <li key={item.id} className="relative rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                    <img
                      src={item.previewUrl}
                      alt={item.file.name}
                      className="h-36 w-full rounded-md bg-gray-50 object-contain"
                    />
                    <p className="mt-2 truncate text-xs text-gray-600" title={item.file.name}>{item.file.name}</p>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-gray-500 shadow hover:text-red-600"
                    >
                      <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Remove {item.file.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={() => setStep(1)}
              disabled={items.length === 0}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="mt-8">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-900">Apply to all files</p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              {metadataFields(batchMeta, (patch) => setBatchMeta((prev) => ({ ...prev, ...patch })), 'batch')}
              <button
                type="button"
                onClick={applyBatchMetadata}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Apply to all
              </button>
            </div>
          </div>

          <ul className="mt-6 space-y-4">
            {items.map((item) => {
              const errors = validationById.get(item.id) || [];
              return (
                <li key={item.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-start gap-4">
                    <img
                      src={item.previewUrl}
                      alt={item.file.name}
                      className="h-24 w-16 rounded-md bg-gray-50 object-contain"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900" title={item.file.name}>{item.file.name}</p>
                      {item.hash && item.status !== 'error' && (
                        <p className="mt-1 text-xs text-green-700">
                          Uploaded — editing the details queues a re-upload. To change the type, remove this file and add it again.
                        </p>
                      )}
                      {item.status === 'error' && item.error && (
                        <p className="mt-1 text-xs text-red-600">{item.error}</p>
                      )}
                      <div className="mt-3">
                        {metadataFields(item.metadata, (patch) => updateMetadata(item.id, patch), `item-${item.id}`, { lockType: !!item.hash })}
                      </div>
                      {showValidation && errors.length > 0 && (
                        <ul className="mt-2 text-sm text-red-600">
                          {errors.map((error) => <li key={error}>{error}</li>)}
                        </ul>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      title="Remove file"
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      <span className="sr-only">Remove {item.file.name}</span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {showValidation && !allMetadataValid && (
            <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800">
              Fix the highlighted files before continuing.
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <button
              type="button"
              onClick={() => setStep(0)}
              className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleContinueFromMetadata}
              disabled={items.length === 0}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
            >
              <ArrowUpTrayIcon className="h-5 w-5" aria-hidden="true" />
              Continue to upload
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-8">
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-4">
                  <img
                    src={item.previewUrl}
                    alt={item.file.name}
                    className="h-16 w-12 rounded-md bg-gray-50 object-contain"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900" title={item.file.name}>{item.file.name}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {item.metadata.type === 'tanner'
                        ? `Tanner · ${item.metadata.gender} · stage ${item.metadata.tanner}`
                        : `Control #${item.metadata.control} · group ${item.metadata.controlGroup}`}
                    </p>
                    {item.status === 'uploading' && (
                      <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-indigo-600 transition-all duration-200"
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                    )}
                    {item.status === 'error' && item.error && (
                      <p className="mt-1 text-sm text-red-600">{item.error}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(item)}
                    {item.status === 'error' && (
                      item.blobOrphaned ? (
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          disabled={uploading}
                          className="inline-flex items-center gap-x-1 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                          Retry cleanup
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => runUploads([item.id])}
                          disabled={uploading}
                          className="inline-flex items-center gap-x-1 rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                          Retry
                        </button>
                      )
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={uploading}
                className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Back
              </button>
              {failedItems.length > 0 && !uploading && (
                <button
                  type="button"
                  onClick={() => runUploads(failedItems.map((item) => item.id))}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
                  Retry {failedItems.length} failed
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setStep(3)}
              disabled={!allSettled}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
            >
              {uploading ? 'Uploading…' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="mt-8">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900">Batch summary</h2>
            <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="rounded-md bg-green-50 p-3">
                <dt className="text-xs font-medium text-green-700">Uploaded</dt>
                <dd className="mt-1 text-2xl font-semibold text-green-900">{summary.uploaded}</dd>
                <dd className="text-xs text-green-700">{summary.tannerUploaded} tanner · {summary.controlUploaded} control</dd>
              </div>
              <div className="rounded-md bg-red-50 p-3">
                <dt className="text-xs font-medium text-red-700">Failed</dt>
                <dd className="mt-1 text-2xl font-semibold text-red-900">{summary.failed}</dd>
              </div>
              <div className="rounded-md bg-amber-50 p-3">
                <dt className="text-xs font-medium text-amber-700">Awaiting labels</dt>
                <dd className="mt-1 text-2xl font-semibold text-amber-900">
                  {labelStatusLoading ? '…' : unlabeledBatchHashes.length}
                </dd>
              </div>
            </dl>

            {summary.failed > 0 && (
              <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {summary.failed} file{summary.failed === 1 ? '' : 's'} failed to upload. Go back to the upload step to retry.
              </div>
            )}

            <p className="mt-4 text-sm text-gray-700">
              {batchTannerHashes.length > 0
                ? 'Tanner stimuli can be labeled with FACE, SEX_1, and SEX_2 regions or marked complete manually. '
                : summary.tannerFailed > 0
                  ? `${summary.tannerFailed === summary.tannerTotal ? 'All' : 'Some'} Tanner uploads in this batch failed — go back to the upload step and retry them before labeling. `
                  : 'This batch contains no Tanner stimuli, so no labeling is needed. '}
              Label status is independent of device availability; use the Stimuli page to manage release state.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {unlabeledBatchHashes.length > 0 && (
                <button
                  type="button"
                  onClick={startLabeling}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500"
                >
                  <TagIcon className="h-5 w-5" aria-hidden="true" />
                  Label {unlabeledBatchHashes.length} stimul{unlabeledBatchHashes.length === 1 ? 'us' : 'i'} now
                </button>
              )}
              <button
                type="button"
                onClick={refreshLabelStatus}
                disabled={labelStatusLoading || batchTannerHashes.length === 0}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
                Refresh label status
              </button>
              <button
                type="button"
                onClick={() => navigate('/stimuli')}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Done — back to Stimuli
              </button>
            </div>
          </div>

          <div className="mt-6 flex justify-start">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Back to uploads
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
